import React, {
  LegacyRef,
  PropsWithChildren,
  ReactElement,
  useEffect,
  useRef,
  useState,
} from "react";
import styles from "./userReviews.module.scss";
import { Star01 } from "@untitled-ui/icons-react";
import classNames from "classnames";
import { Divider } from "@kunduz/ui";
import { gray } from "../../../theme";

export const Title = (props: PropsWithChildren) => {
  return <div className={styles.title}>{props.children}</div>;
};

export const MetaAndPercWrapper = (props: PropsWithChildren) => {
  return <div className={styles.metaAndPercWrapper}>{props.children}</div>;
};

export const Meta = (props: {
  rating?: string;
  reviewLabel?: string;
}): ReactElement => {
  function isStarFilled(index: number) {
    if (!props.rating) return false;
    return index - 0.5 <= parseFloat(props.rating.replace(",", "."));
  }

  return (
    <div className={styles.metaWrapper}>
      {props.rating && <p className={styles.rating}>{props.rating}</p>}
      {props.rating !== undefined && (
        <div className={styles.ratingWrapper}>
          <div className={styles.starWrapper}>
            {Array.from(Array(5)).map((s, t) => (
              <Star01
                key={`star_${t}`}
                color="#FEC84B"
                width={20}
                height={20}
                className={classNames({
                  [styles.filled]: isStarFilled(t + 1),
                })}
              />
            ))}
          </div>
        </div>
      )}
      {props.reviewLabel && (
        <div className={styles.reviewLabel}>{props.reviewLabel}</div>
      )}
    </div>
  );
};

export const Percentages = (props: {
  five: string;
  four: string;
  three: string;
  two: string;
  one: string;
}) => {
  return (
    <div className={styles.percWrapper}>
      <div className={styles.percBar}>
        <div className={styles.label}>5</div>
        <div className={styles.bar}>
          <div className={styles.filled} style={{ width: `${props.five}%` }} />
        </div>
      </div>
      <div className={styles.percBar}>
        <div className={styles.label}>4</div>
        <div className={styles.bar}>
          <div className={styles.filled} style={{ width: `${props.four}%` }} />
        </div>
      </div>
      <div className={styles.percBar}>
        <div className={styles.label}>3</div>
        <div className={styles.bar}>
          <div className={styles.filled} style={{ width: `${props.three}%` }} />
        </div>
      </div>
      <div className={styles.percBar}>
        <div className={styles.label}>2</div>
        <div className={styles.bar}>
          <div className={styles.filled} style={{ width: `${props.two}%` }} />
        </div>
      </div>
      <div className={styles.percBar}>
        <div className={styles.label}>1</div>
        <div className={styles.bar}>
          <div className={styles.filled} style={{ width: `${props.one}%` }} />
        </div>
      </div>
    </div>
  );
};

export const ReviewCardWrapper = (props: PropsWithChildren) => {
  return <div className={styles.reviewCardWrapper}>{props.children}</div>;
};

export const ReviewCard = (props: {
  imageUrl?: string;
  title?: string;
  description?: string;
  rating?: string;
  reviewTitle?: string;
  reviewDesc?: string;
  onShowMoreClicked?: () => void;
}) => {
  const [canExpand, setCanExpand] = useState(false);
  const [expanded, setExpanded] = useState(false);
  function isStarFilled(index: number) {
    if (!props.rating) return false;
    return index <= parseFloat(props.rating);
  }
  const descriptionDivRef = useRef<LegacyRef<HTMLDivElement>>();

  useEffect(() => {
    if (!descriptionDivRef.current) return;

    function showReadMoreButton(element: HTMLDivElement) {
      if (!element) return false;
      if (
        element.offsetHeight < element.scrollHeight ||
        element.offsetWidth < element.scrollWidth
      ) {
        return true;
      } else {
        return false;
      }
    }

    setCanExpand(showReadMoreButton(descriptionDivRef.current as any));
  }, [descriptionDivRef.current]);

  return (
    <div className={styles.reviewCard}>
      <div className={styles.reviewCardMeta}>
        {props.imageUrl && <img src={props.imageUrl} alt="" />}
        <div>
          {props.title && <p className={styles.title}>{props.title}</p>}
          {props.description && (
            <p className={styles.description}>{props.description}</p>
          )}
        </div>
      </div>
      <Divider top={0} bottom={0} color={gray[200]} />
      <div className={styles.reviewCardContent}>
        {props.rating !== undefined && (
          <div className={styles.ratingWrapper}>
            <div className={styles.starWrapper}>
              {Array.from(Array(5)).map((s, t) => (
                <Star01
                  key={`star_${t}`}
                  color="#FEC84B"
                  width={20}
                  height={20}
                  className={classNames({
                    [styles.filled]: isStarFilled(t + 1),
                  })}
                />
              ))}
            </div>
          </div>
        )}
        {props.reviewTitle && (
          <div
            className={styles.reviewTitle}
            dangerouslySetInnerHTML={{ __html: props.reviewTitle }}
          ></div>
        )}
        {props.reviewDesc && (
          <div className={styles.descriptionRelativeContainer}>
            <div
              className={classNames(styles.reviewDesc, {
                [styles.expanded]: expanded,
              })}
              dangerouslySetInnerHTML={{ __html: props.reviewDesc }}
              ref={descriptionDivRef as any}
            ></div>
            {canExpand && !expanded && (
              <div className={styles.showMorePosition}>
                <div className={styles.gradient}></div>
                <div
                  className={styles.showMoreButton}
                  onClick={(e) => {
                    setExpanded(true);
                    props.onShowMoreClicked && props.onShowMoreClicked();
                  }}
                >
                  daha fazla
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export const LoadMoreButton = (
  props: PropsWithChildren<{ onClick: () => void; disabled: boolean }>,
) => {
  return (
    <div className={styles.loadMoreButtonWrapper}>
      <button
        className={styles.loadMoreButton}
        onClick={props.onClick}
        disabled={props.disabled}
      >
        {props.children}
      </button>
    </div>
  );
};

export const PlanDetailUserReviews = {
  Title,
  MetaAndPercWrapper,
  Meta,
  Percentages,
  ReviewCardWrapper,
  ReviewCard,
  LoadMoreButton,
};
